import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Enums } from '@cyborg-monorepo/hunter/common';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { CyborgFilterComponent } from '../../../shared/components/cyborg-filter/cyborg-filter.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import {
  DEFAULT_SEARCH_STATE,
  SearchStateExtended,
  SearchStateService,
  TagType,
} from '../../services/search-state.service';
import { SearchUrlService } from '../../services/search-url.service';

@Component({ standalone: false, 
  templateUrl: './filter.component.html',
  styleUrls: ['filter.component.scss'],
})
export class FilterComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(HeaderComponent) public header: HeaderComponent;
  @ViewChild(CyborgFilterComponent) public cyborgFilter: CyborgFilterComponent;

  public cyborgIndexEnum = Enums.CyborgIndexEnum;
  public filteredTags: TagType[] = [];
  public filteredMitre: TagType[] = [];
  public filteredKillChains: TagType[] = [];
  public filteredTools: TagType[] = [];
  public filteredPlatformTypes: TagType[] = [];
  public filteredDataSources: TagType[] = [];
  public filteredDependencyTypes: TagType[] = [];
  public filteredGoals: TagType[] = [];
  public term = ['*'];
  public touched: boolean;

  public contextualTagsSubject: Subject<string> = new Subject<string>();
  public mitreTagsSubject: Subject<string> = new Subject<string>();
  public huntPackageCount = 0;
  public profileCount = 0;
  public collectionCount = 0;
  public searchState$: BehaviorSubject<SearchStateExtended>;
  public library: Enums.CyborgIndexEnum[];
  public searchForm: FormGroup;

  private stateSubscription: Subscription;
  private resultsSubscription: Subscription;

  constructor(
    private http: HttpClient,
    public searchStateService: SearchStateService,
    private changeDetectorRef: ChangeDetectorRef,
    private searchUrlService: SearchUrlService,
  ) {}

  public ngOnInit(): void {
    this.searchForm = new FormGroup({
      search: new FormControl(''),
    });
    this.searchState$ = this.searchStateService.state;

    // TODO: Move to | async pipes in template
    // TODO: Trim pass-through component parameters just directly used in template
    this.stateSubscription = this.searchStateService.state.subscribe(
      (state: SearchStateExtended) => {
        this.term = state.term;
        this.library = state.library;
        this.touched = state.touched;
      },
    );

    this.resultsSubscription = this.searchStateService.results.subscribe(
      (results) => {
        this.filteredMitre = results.facets.mitreTags;
        this.filteredKillChains = results.facets.killChains;

        this.filteredTags = results.facets.tags.sort((a, b) => {
          const typeA = a.type.toLowerCase();
          const typeB = b.type.toLowerCase();

          if (typeA < typeB) return -1;
          if (typeA > typeB) return 1;
          return 0;
        });

        this.filteredTools = results.facets.tools;

        const behavior = results.facets.goals[0]?.tags.find((tag) => {
          return tag.key == 'Behavior';
        });
        const hunt = results.facets.goals[0]?.tags.find((tag) => {
          return tag.key == 'Hunt';
        });
        const visual = results.facets.goals[0]?.tags.find((tag) => {
          return tag.key == 'Visual';
        });
        const targeted = results.facets.goals[0]?.tags.find((tag) => {
          return tag.key == 'Targeted';
        });
        const newTags: TagType[] = [
          { type: 'Hunt', tags: [] },
          { type: 'Detection', tags: [] },
        ];

        if (behavior) {
          newTags[1].tags.push(behavior);
        }
        if (targeted) {
          newTags[1].tags.push(targeted);
        }
        if (hunt) {
          newTags[0].tags.push(hunt);
        }
        if (visual) {
          newTags[0].tags.push(visual);
        }
        this.filteredGoals = newTags;

        this.filteredPlatformTypes = results.facets.platformTypes;
        this.filteredDataSources = results.facets.dataSources;
        this.filteredDependencyTypes = results.facets.dependencies;

        const counts = results.data.count;

        this.huntPackageCount = counts.cyborg_hunt_packages ?? 0;
        this.profileCount = counts.cyborg_threat_profile ?? 0;
        this.collectionCount = counts.cyborg_collections ?? 0;
      },
    );

    this.showFilterPane();
  }

  public ngOnDestroy(): void {
    this.stateSubscription?.unsubscribe();
    this.resultsSubscription?.unsubscribe();
  }

  public ngAfterViewInit(): void {
    // Workaround for NG0100: ExpressionChangedAfterItHasBeenCheckedError while initial state setup is still dicey
    // https://angular.io/errors/NG0100
    this.changeDetectorRef.detectChanges();
  }

  public onLibraryFilter(target: Enums.CyborgIndexEnum): void {
    if (!this.touched) {
      this.searchStateService.setLibrary([target], true);
    } else {
      if (!this.library.includes(target)) {
        this.searchStateService.addLibrary(target, true);
      } else {
        if (this.library.length > 1) {
          this.searchStateService.removeLibrary(target, true);
        } else {
          this.searchStateService.setLibrary(
            DEFAULT_SEARCH_STATE.library,
            false,
          );
        }
      }
    }
  }

  public onContextualSearch(target: EventTarget): void {
    const value = (target as HTMLInputElement).value;
    this.searchStateService.results.pipe(take(1)).subscribe((resultState) => {
      this.filteredTags = resultState.facets.tags.filter((section) => {
        return section.tags.some((tag) => {
          return tag.key.toLowerCase().includes(value.trim().toLowerCase());
        });
      });
      this.changeDetectorRef.detectChanges();
    });
    this.contextualTagsSubject.next(value);
  }

  public onMitreSearch(target: EventTarget): void {
    const value = (target as HTMLInputElement).value;
    this.searchStateService.results.pipe(take(1)).subscribe((resultState) => {
      this.filteredMitre = resultState.facets.mitreTags.filter((section) => {
        return section.tags.some((tag) => {
          return tag.key.toLowerCase().includes(value.trim().toLowerCase());
        });
      });
      this.changeDetectorRef.detectChanges();
    });
    this.mitreTagsSubject.next(value);
  }

  public toggleFilterPane(): void {
    const filterSide = document.querySelector(
      `#dashboard main`,
    ) as HTMLInputElement;
    filterSide.classList.toggle('is-showing-filters');
  }

  public showFilterPane(): void {
    const filterSide = document.querySelector(
      `#dashboard main`,
    ) as HTMLInputElement;
    filterSide.classList.remove('is-showing-filters');
  }

  public hideFilterPane(): void {
    const filterSide = document.querySelector(
      `#dashboard main`,
    ) as HTMLInputElement;
    filterSide.classList.add('is-showing-filters');
  }

  public onContentClose(): void {
    const charts = document.getElementById('charts') as HTMLElement;
    charts.classList.toggle('is-closed');
  }

  public onCompatible(target: EventTarget): void {
    if ((target as HTMLInputElement).checked)
      this.searchStateService.setCompatible();
    else this.searchStateService.resetCompatible();
  }

  public onLastUpdated(event: EventTarget): void {
    const target = event as HTMLInputElement;
    let lastUpdate = Number(target.value);
    const selectedTimes = document.querySelectorAll(
      `aside input[type="checkbox"]:not([value="${target.value}"])`,
    );
    for (let i = 0; i < selectedTimes.length; i += 1) {
      const times = selectedTimes[i] as HTMLInputElement;
      times.checked = false;
    }
    if (target.checked === false) {
      lastUpdate = 365;
    }
    this.searchStateService.setLastUpdated(lastUpdate);
  }

  public onSearch(): void {
    const term: string = this.searchForm.value.search;
    if (term) {
      if (!this.searchStateService.state.value.term.includes(term)) {
        this.searchStateService.setTerm(term);
        this.searchUrlService.gotoUrl(this.searchStateService.state.value);
      }
      this.searchForm.setValue({ search: '' });
    }
  }
}
